import { useCallback, useMemo, useRef, useState } from "react";

/**
 * Row checkboxes on a client-filtered list, Explorer-style:
 * - a plain click toggles one row (no Ctrl needed — that's what a checkbox already is),
 * - Shift+click sets every VISIBLE row from the anchor (last plain click) to the
 *   clicked one to the anchor's state; the anchor stays put, like in Windows,
 * - "select all" works on the visible rows only.
 *
 * The selection survives filter changes, but everything the page acts on
 * (`selectedIds`, counts, the header checkbox) is the visible ∩ selected part, in
 * `visibleIds` order — so an export never carries rows the user can't see.
 */
export function useRowSelection(visibleIds: string[]) {
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const anchorRef = useRef<string | null>(null);

  const selectedIds = useMemo(
    () => visibleIds.filter((id) => selected.has(id)),
    [visibleIds, selected],
  );
  const allSelected = visibleIds.length > 0 && selectedIds.length === visibleIds.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggle = useCallback(
    (id: string, shiftKey = false) => {
      const anchor = anchorRef.current;
      const from = anchor === null ? -1 : visibleIds.indexOf(anchor);
      const to = visibleIds.indexOf(id);
      const range = shiftKey && from >= 0 && to >= 0 && anchor !== id;
      if (!range) anchorRef.current = id;
      setSelected((prev) => {
        const next = new Set(prev);
        if (!range) {
          if (next.has(id)) next.delete(id);
          else next.add(id);
          return next;
        }
        const select = prev.has(anchor!);
        for (const rowId of visibleIds.slice(Math.min(from, to), Math.max(from, to) + 1)) {
          if (select) next.add(rowId);
          else next.delete(rowId);
        }
        return next;
      });
    },
    [visibleIds],
  );

  const toggleAll = useCallback(() => {
    setSelected((prev) => {
      const next = new Set(prev);
      const everyVisible = visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
      for (const id of visibleIds) {
        if (everyVisible) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  }, [visibleIds]);

  const isSelected = useCallback((id: string) => selected.has(id), [selected]);

  return { isSelected, selectedIds, allSelected, someSelected, toggle, toggleAll };
}
